import React, { useState, useEffect } from 'react';

/**
 * Utility functions for obfuscating contact information
 */

// Base64 encode helper
export const encodeContact = (contact) => {
  return btoa(contact);
};

// Base64 decode helper
export const decodeContact = (encoded) => {
  try {
    return atob(encoded);
  } catch (e) {
    return '';
  }
};

/**
 * Protected Email Component
 * Displays email as clickable mailto link but obfuscated from scrapers
 *
 * @param {string} email - Base64 encoded email address
 * @param {string} className - Optional CSS class
 * @param {object} style - Optional inline styles
 * @param {React.ReactNode} children - Optional children (if not provided, decoded email is displayed)
 */
export const ProtectedEmail = ({ email, className = '', style = {}, children, subject = '' }) => {
  const [decoded, setDecoded] = useState('');

  useEffect(() => {
    // Decode on client side only, not during SSR
    setDecoded(decodeContact(email));
  }, [email]);

  if (!decoded) {
    // Show loading state or placeholder
    return <span className={className} style={style}>...</span>;
  }

  const mailtoLink = subject
    ? `mailto:${decoded}?subject=${encodeURIComponent(subject)}`
    : `mailto:${decoded}`;

  return (
    <a
      href={mailtoLink}
      className={className}
      style={style}
      onClick={(e) => {
        // Let the browser handle mailto: naturally
        // This ensures it works with default email clients
      }}
    >
      {children || decoded}
    </a>
  );
};

/**
 * Protected Phone Component
 * Displays phone as clickable tel link but obfuscated from scrapers
 *
 * @param {string} phone - Base64 encoded phone number (format for tel: link, e.g., "041230414")
 * @param {string} display - Base64 encoded display format (e.g., "041/230 44 14")
 * @param {string} className - Optional CSS class
 * @param {object} style - Optional inline styles
 * @param {React.ReactNode} children - Optional children (if not provided, decoded display is shown)
 */
export const ProtectedPhone = ({ phone, display, className = '', style = {}, children }) => {
  const [decodedPhone, setDecodedPhone] = useState('');
  const [decodedDisplay, setDecodedDisplay] = useState('');

  useEffect(() => {
    // Decode on client side only
    setDecodedPhone(decodeContact(phone));
    setDecodedDisplay(display ? decodeContact(display) : decodeContact(phone));
  }, [phone, display]);

  if (!decodedPhone) {
    // Show loading state or placeholder
    return <span className={className} style={style}>...</span>;
  }

  return (
    <a
      href={`tel:${decodedPhone}`}
      className={className}
      style={style}
    >
      {children || decodedDisplay}
    </a>
  );
};

/**
 * Protected Text Component (for non-clickable contact display)
 * Useful for showing contact info without making it a link
 *
 * @param {string} text - Base64 encoded text
 * @param {string} className - Optional CSS class
 * @param {object} style - Optional inline styles
 */
export const ProtectedText = ({ text, className = '', style = {}, as: Component = 'span' }) => {
  const [decoded, setDecoded] = useState('');

  useEffect(() => {
    setDecoded(decodeContact(text));
  }, [text]);

  if (!decoded) {
    return <Component className={className} style={style}>...</Component>;
  }

  return (
    <Component className={className} style={style}>
      {decoded}
    </Component>
  );
};

/**
 * Pre-encoded contact information for INAQ
 * Import these constants to use throughout the application
 */
export const INAQ_CONTACTS = {
  // Emails
  MARKETING_EMAIL: encodeContact('marketing@inaq.sk'),
  CAREERS_EMAIL: encodeContact('careers@inaq.sk'),
  NEWSLETTER_EMAIL: encodeContact('newsletter@inaq.sk'),

  // Phone numbers
  PHONE_TEL: encodeContact('041230414'), // Format for tel: links
  PHONE_DISPLAY: encodeContact('041/230 44 14'), // Format for display
  PHONE_INTERNATIONAL: encodeContact('+421412304414'), // International format
};

export default {
  ProtectedEmail,
  ProtectedPhone,
  ProtectedText,
  encodeContact,
  decodeContact,
  INAQ_CONTACTS,
};
